import isUndefined from 'lodash/isUndefined';
import sortBy from 'lodash/sortBy';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import {window} from 'ssr-window';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import AlertModal from 'common/alert/AlertModal';
import SHLoader from 'common/loader/SHLoader';
import {setLocation} from 'search-request/search-request-actions';
import {maintainSelectedSpot} from 'spot/spot-actions';
import {getSpotsMobile} from 'spots/spots-actions';
import WithSearch from './WithSearch';
import SpotListContainerMobile from './spot-list/SpotListContainerMobile';
import SingleTermSearchControls from './controls/SingleTermSearchControls';

const SpotDetailsPanelMobile = loadable(() =>
    import('spot-details/SpotDetailsPanelMobile')
);
const SearchMap = loadable(() => import('./map/SearchMap'));
const EventList = loadable(() => import('event/EventList'));

export class SearchMobile extends Component {
    static propTypes = {
        searchRequestId: PropTypes.string,
        searchUrl: PropTypes.string,
        isMonthly: PropTypes.bool,
        spots: PropTypes.arrayOf(PropTypes.object),
        findParkingNearMe: PropTypes.func,
        selectedSpot: PropTypes.object,
        shouldMaintainSelectedSpot: PropTypes.bool,
        setShouldMaintainSelectedSpot: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func,
        monthlyPhoneNumber: PropTypes.string.isRequired,
        monthlyPhoneNumberFriendly: PropTypes.string.isRequired,
        loadMobileSpots: PropTypes.func.isRequired,
        isFetchComplete: PropTypes.bool.isRequired,
        numActiveFilters: PropTypes.number,
        idealDistance: PropTypes.number,
        mobilePaginationLimit: PropTypes.number,
        isMobileListViewActive: PropTypes.bool.isRequired,
        sortListBy: PropTypes.string.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        geolocated: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        onToggleEventList: PropTypes.func,
        setNewSearchLocation: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const {selectedSpot, geolocated, searchUrl} = props;

        this._isBrowser = EnvironmentUtils.isBrowser();
        this._minDistance = 0;
        this._pageCount = 1;
        this._spotLoaderEnabled = true;

        // TODO: update for new URL structure?
        // In order to test effect of content on SEO and conversion, only show on these pages
        this._hasSeoContent = [
            '/chicago-parking',
            '/nyc-parking',
            '/philadelphia-parking',
        ].includes(searchUrl);

        this.state = {
            showListLoader: false,
            showLoadingComplete: false,
            minimizeSearchDetails: false,
            showGeolocateModal: this._isBrowser
                ? Boolean(
                      !geolocated &&
                          window.location.pathname.match(/parking-near-me/)
                  )
                : false,
            selectedSpot,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {shouldMaintainSelectedSpot, selectedSpot} = nextProps;

        if (!shouldMaintainSelectedSpot) {
            return {
                selectedSpot,
            };
        }

        return null;
    }

    componentDidMount() {
        const {isMobileListViewActive} = this.props;

        if (!this.state.showGeolocateModal && isMobileListViewActive) {
            this._newSearch({
                initial: true,
            });
        }

        this._cacheSelectors();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isFetchComplete,
            isMonthly,
            searchRequestId,
            starts,
            ends,
            isMobileListViewActive,
            geolocated,
        } = this.props;
        const {
            isFetchComplete: prevIsFetchComplete,
            isMonthly: prevIsMonthly,
            searchRequestId: prevSearchRequestId,
            starts: prevStarts,
            ends: prevEnds,
            isMobileListViewActive: prevIsMobileListViewActive,
            geolocated: prevGeolocated,
        } = prevProps;

        if (!prevIsFetchComplete && isFetchComplete) {
            this._fetchCompleted();
        }

        if (prevSearchRequestId !== searchRequestId) {
            this._newSearch({
                initial: false,
            });
        } else if (prevIsMonthly !== isMonthly) {
            this._minDistance = 0;
            this._pageCount = 1;
            this._newSearch({
                initial: false,
            });
        } else {
            if (
                (isMonthly && prevStarts !== starts) ||
                (!isMonthly && (prevStarts !== starts || prevEnds !== ends))
            ) {
                this._minDistance = 0;
                this._pageCount = 1;

                this._newSearch({
                    dateTimeChanged: true,
                });
            }
        }

        if (prevIsMobileListViewActive !== isMobileListViewActive) {
            this._showSearchDetails();
        }

        if (!prevIsMobileListViewActive && isMobileListViewActive) {
            this._cacheSelectors();
        }

        if (!prevGeolocated && geolocated) {
            this._newSearch({
                initial: true,
            });
        }
    }

    componentWillUnmount() {
        if (this._spotsLoader) {
            this._spotsLoader.cancel();
        }

        this._spotLoaderEnabled = false;
    }

    _onSpotDetailsHidden = () => {
        const {onSpotDetailsHidden, setShouldMaintainSelectedSpot} = this.props;

        setShouldMaintainSelectedSpot(false);

        if (onSpotDetailsHidden) {
            onSpotDetailsHidden();
        }
    };

    _onSpotDetailsPanelSearchClick = () => {
        this.props.setShouldMaintainSelectedSpot(true);
    };

    _onConfirmGeolocate = () => {
        this.setState({
            showListLoader: true,
        });

        if (!isUndefined(window) && window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(
                this._userPositionReturned,
                () => {
                    // eslint-disable-next-line no-alert
                    alert(
                        'Sorry, your browser does not allow geolocation, please search an address.'
                    );
                    this._onRejectGeolocate();
                }
            );
        } else {
            // eslint-disable-next-line no-alert
            alert(
                'Sorry, your browser does not support geolocation, please search an address.'
            );
            this._onRejectGeolocate();
        }
    };

    _onRejectGeolocate = () => {
        this._onLoadingComplete();
    };

    _onLoadingComplete = () => {
        this.setState({
            showListLoader: false,
            showLoadingComplete: true,
        });
    };

    _onShowGeolocateModalHidden = () => {
        this.setState({
            showGeolocateModal: false,
        });
    };

    _onListScroll = () => {
        const {sortListBy} = this.props;
        const {showListLoader} = this.state;
        const scrollTop = this._spotListEl.scrollTop;

        if (
            !this._hasSeoContent &&
            !showListLoader &&
            sortListBy !== 'price' &&
            this._spotListEl.scrollTop ===
                this._spotListEl.scrollHeight - this._spotListEl.offsetHeight
        ) {
            this._loadNextPage();
        }

        this.setState({
            minimizeSearchDetails:
                scrollTop > this._singleTermSearchControlsEl.offsetHeight,
        });
    };

    _cacheSelectors() {
        this._spotListEl = DOMUtils.el('.SpotListContainer');
        this._singleTermSearchControlsEl = DOMUtils.el(
            '.SingleTermSearchControls'
        );
    }

    _showSearchDetails() {
        this.setState({
            minimizeSearchDetails: false,
        });
    }

    _fetchCompleted() {
        this.setState({
            showListLoader: false,
            showLoadingComplete: false,
        });
    }

    _newSearch = ({dateTimeChanged = false, initial = false}) => {
        const {numActiveFilters, idealDistance, loadMobileSpots} = this.props;

        if (!initial) {
            this.setState({
                showListLoader: true,
            });
        }

        loadMobileSpots({
            limit: 8,
            maxDistance: idealDistance,
            dateTimeChanged,
        }).then(response => {
            if (!this._spotLoaderEnabled) {
                return;
            }

            const {
                value: {results},
            } = response;

            if (!results || results.length < 4 || numActiveFilters > 0) {
                if (!results) {
                    this._minDistance = 100;
                }

                // Ccccccrazy repeat tries for additional spots when none found initially
                // to be updated when we have real search pagination
                this._loadNextPage({
                    onNoResults: () => {
                        this._loadNextPage({
                            onNoResults: () => {
                                this._loadNextPage({
                                    onNoResults: () => {
                                        this._onLoadingComplete();
                                    },
                                });
                            },
                        });
                    },
                });
            }
        });
    };

    _loadNextPage = ({onNoResults = null} = {}) => {
        const {
            spots,
            loadMobileSpots,
            numActiveFilters,
            idealDistance,
            mobilePaginationLimit,
        } = this.props;
        let minDistance = spots.length
            ? sortBy(spots, 'distance').reverse()[0].distance - 30
            : 0;

        if (this._pageCount > 9) {
            this._onLoadingComplete();

            return;
        }

        minDistance =
            this._minDistance && this._minDistance === minDistance
                ? minDistance + 500
                : minDistance < this._minDistance
                ? this._minDistance + 500
                : minDistance;

        this.setState({
            showListLoader: true,
        });

        const params = {
            minDistance,
            maxDistance: idealDistance,
        };

        if (mobilePaginationLimit) {
            params.limit = mobilePaginationLimit;
        }

        this._spotsLoader = APIUtils.makeCancelable(loadMobileSpots(params));

        this._spotsLoader.promise
            .then(({value}) => {
                if (isUndefined(value.data)) {
                    return;
                }

                const {
                    data: {
                        data: {results},
                    },
                } = value;

                if (
                    (!results || !results.length || numActiveFilters > 0) &&
                    onNoResults
                ) {
                    onNoResults();
                }
            })
            .catch(error => {
                return error;
            });

        this._minDistance = minDistance;
        this._pageCount += 1;
    };

    _userPositionReturned = response => {
        if (!response.coords) {
            return false;
        }

        const {latitude, longitude} = response.coords;

        this.props.setNewSearchLocation({
            latitude,
            longitude,
            title: 'Current Location',
            geolocated: true,
        });

        this.setState({
            showGeolocateModal: false,
        });
    };

    render() {
        const {
            searchRequestId,
            isMonthly,
            spots,
            findParkingNearMe,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            geolocated,
            isMobileListViewActive,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
        } = this.props;
        const {showGeolocateModal, minimizeSearchDetails} = this.state;
        const {selectedSpot, showListLoader, showLoadingComplete} = this.state;
        const searchClasses = classNames(
            'Search',
            {'Search-monthly': isMonthly},
            {'Search-list-view': isMobileListViewActive},
            {'Search-map-view': !isMobileListViewActive},
            {'Search-minimize-details': minimizeSearchDetails}
        );

        return (
            <>
                <div className="main" role="main">
                    <div className={searchClasses}>
                        <div className="search-results">
                            {searchRequestId ? (
                                <>
                                    {!isMobileListViewActive && (
                                        <>
                                            <div className="SearchMap-container">
                                                <SearchMap
                                                    isMobile
                                                    spots={spots}
                                                    monthlyPhoneNumber={
                                                        monthlyPhoneNumber
                                                    }
                                                    monthlyPhoneNumberFriendly={
                                                        monthlyPhoneNumberFriendly
                                                    }
                                                    findParkingNearMe={
                                                        findParkingNearMe
                                                    }
                                                />
                                            </div>
                                            <SingleTermSearchControls
                                                isMapShowing
                                                showDestinationInfoModal={
                                                    !this._hasSeoContent
                                                }
                                            />
                                        </>
                                    )}
                                    {isMobileListViewActive && (
                                        <div className="list">
                                            <SpotListContainerMobile
                                                spots={spots}
                                                showListLoader={showListLoader}
                                                showLoadingComplete={
                                                    showLoadingComplete
                                                }
                                                isEventListEnabled={
                                                    isEventListEnabled
                                                }
                                                onToggleEventList={
                                                    onToggleEventList
                                                }
                                                loadNextPage={
                                                    this._loadNextPage
                                                }
                                                isMapShowing={
                                                    !isMobileListViewActive
                                                }
                                                showDestinationInfo={
                                                    this._hasSeoContent
                                                }
                                                onListScroll={
                                                    this._onListScroll
                                                }
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <SHLoader />
                            )}
                        </div>
                        {selectedSpot && (
                            <SpotDetailsPanelMobile
                                spot={selectedSpot}
                                onSearchClick={
                                    this._onSpotDetailsPanelSearchClick
                                }
                                onSpotDetailsHidden={this._onSpotDetailsHidden}
                            />
                        )}
                    </div>
                </div>
                {showGeolocateModal && !geolocated && (
                    <AlertModal
                        className="GeolocationModal"
                        title="Where ya headed?"
                        confirmButtonLabel="My Current Location"
                        showRejectButton
                        showClose={false}
                        rejectButtonLabel="Somewhere Else"
                        onHidden={this._onShowGeolocateModalHidden}
                        onConfirm={this._onConfirmGeolocate}
                        onReject={this._onRejectGeolocate}
                    />
                )}
                {showEventList && (
                    <EventList onToggleEventList={onToggleEventList} />
                )}
            </>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {
            data: {
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
            },
        },
        filters: {numActive},
        spot: {shouldMaintainSelectedSpot},
        spots: {isPending: areSpotsLoading},
        search: {
            data: {
                geolocated,
                mobilePaginationLimit,
                isMobileListViewActive,
                sortBy: sortListBy,
            },
        },
        searchRequest: {id, starts, ends},
    } = state;

    return {
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        searchRequestId: id,
        starts,
        ends,
        isFetchComplete: !areSpotsLoading,
        geolocated,
        numActiveFilters: numActive,
        sortListBy,
        mobilePaginationLimit,
        isMobileListViewActive,
        shouldMaintainSelectedSpot,
    };
};

const mapDispatchToProps = {
    loadMobileSpots: getSpotsMobile,
    setNewSearchLocation: setLocation,
    setShouldMaintainSelectedSpot: maintainSelectedSpot,
};

const enhance = compose(
    WithSearch,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SearchMobile);
